import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import { useTranslation } from "react-i18next";

import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import StatusMessage from "../../components/ui/StatusMessage";

export default function AdminCreatePayerWallet() {
  const { t } = useTranslation();

  const [payers, setPayers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [wallets, setWallets] = useState([]);

  const [selectedPayerId, setSelectedPayerId] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");

  const [loadingPayers, setLoadingPayers] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchPayers();
  }, []);

  const getErrorMessage = (err, fallbackKey) => {
    const detail = err?.response?.data?.detail;

    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          const location = Array.isArray(item.loc) ? item.loc.join(".") : "";
          return location ? `${location}: ${item.msg}` : item.msg;
        })
        .filter(Boolean)
        .join(" | ");
    }

    return t(fallbackKey);
  };

  const fetchPayers = async () => {
    setLoadingPayers(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/payer-wallet-candidates`);
      const rows = Array.isArray(res.data) ? res.data : [];

      const uniquePayers = Object.values(
        rows.reduce((acc, payer) => {
          if (payer?.user_id && payer?.role === "payer") {
            acc[payer.user_id] = payer;
          }
          return acc;
        }, {})
      );

      setPayers(uniquePayers);
    } catch (err) {
      console.error("❌ Failed to fetch payers", err);
      setPayers([]);
      setMessage("❌ " + getErrorMessage(err, "admin_create_payer_wallet.messages.fetch_payers_failed"));
      setLastAction("fetch");
    } finally {
      setLoadingPayers(false);
    }
  };

  const fetchMissingRoutesForPayer = async (payerId) => {
    if (!payerId) {
      setRoutes([]);
      return;
    }

    setLoadingRoutes(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/payers/${payerId}/missing-routes`
      );

      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch missing routes for payer", err);
      setRoutes([]);
      setMessage("❌ " + getErrorMessage(err, "admin_create_payer_wallet.messages.fetch_routes_failed"));
      setLastAction("fetch");
    } finally {
      setLoadingRoutes(false);
    }
  };

  const fetchWalletsForPayer = async (payerId) => {
    if (!payerId) {
      setMessage("❌ " + t("admin_create_payer_wallet.messages.missing_payer"));
      setLastAction("wallets");
      return;
    }

    setLoadingWallets(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/all-wallets-of/${payerId}`
      );

      const rows = Array.isArray(res.data) ? res.data : [];
      setWallets(rows);

      setMessage(
        "✅ " +
          t("admin_create_payer_wallet.messages.wallets_loaded") +
          ` (${rows.length})`
      );
      setLastAction("wallets");
    } catch (err) {
      console.error("❌ Failed to fetch wallets for payer", err);
      setWallets([]);
      setMessage("❌ " + getErrorMessage(err, "admin_create_payer_wallet.messages.fetch_wallets_failed"));
      setLastAction("wallets");
    } finally {
      setLoadingWallets(false);
    }
  };

  const handleCreateWallet = async () => {
    if (!selectedPayerId) {
      setMessage("❌ " + t("admin_create_payer_wallet.messages.missing_payer"));
      setLastAction("create");
      return;
    }

    if (!selectedRouteId) {
      setMessage("❌ " + t("admin_create_payer_wallet.messages.missing_route"));
      setLastAction("create");
      return;
    }

    setSubmitting(true);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/admin/payer-route-wallets`, {
        user_id: selectedPayerId,
        route_id: selectedRouteId,
      });

      const wallet = res.data?.wallet;
      const route = res.data?.route;
      const bus = res.data?.bus;
      const payer = res.data?.payer;

      let successMessage = "✅ " + t("admin_create_payer_wallet.messages.success");

      if (payer?.userno) {
        successMessage += ` ${t("admin_create_payer_wallet.messages.payer")}: ${payer.userno}.`;
      }

      if (route?.route_name) {
        successMessage += ` ${t("admin_create_payer_wallet.messages.route")}: ${route.route_name}.`;
      }

      if (bus?.bus_code) {
        successMessage += ` ${t("admin_create_payer_wallet.messages.bus")}: ${bus.bus_code}.`;
      }

      if (wallet?.walletname) {
        successMessage += ` ${t("admin_create_payer_wallet.messages.wallet")}: ${wallet.walletname}.`;
      }

      setMessage(successMessage);
      setLastAction("create");
      setSelectedRouteId(null);

      fetchMissingRoutesForPayer(selectedPayerId);
      fetchWalletsForPayer(selectedPayerId);
    } catch (err) {
      console.error("❌ Error creating payer wallet", err);
      setMessage("❌ " + getErrorMessage(err, "admin_create_payer_wallet.messages.failure"));
      setLastAction("create");
    } finally {
      setSubmitting(false);
    }
  };

  const payerOptions = payers.map((payer) => ({
    value: payer.user_id,
    label: `${payer.fullname || t("admin_create_payer_wallet.fields.select_payer.empty_name")} (${payer.userno})`,
  }));

  const routeOptions = routes.map((route) => ({
    value: route.route_id,
    label: [
      route.route_name,
      route.start_point && route.end_point
        ? `${route.start_point} → ${route.end_point}`
        : null,
      route.bus_code
        ? `${t("admin_create_payer_wallet.fields.select_route.bus_code")}: ${route.bus_code}`
        : null,
      route.plate_number
        ? `${t("admin_create_payer_wallet.fields.select_route.plate_number")}: ${route.plate_number}`
        : null,
      route.vendor_name
        ? `${t("admin_create_payer_wallet.fields.select_route.vendor")}: ${route.vendor_name}`
        : null,
    ]
      .filter(Boolean)
      .join(" | "),
  }));

  const selectedPayerOption =
    payerOptions.find((option) => option.value === selectedPayerId) || null;

  const selectedRouteOption =
    routeOptions.find((option) => option.value === selectedRouteId) || null;

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_create_payer_wallet.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700 }}>
                  {t("admin_create_payer_wallet.title")}
                </h1>

                <FormField label={t("admin_create_payer_wallet.fields.select_payer.label")}>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={loadingPayers}
                    options={payerOptions}
                    value={selectedPayerOption}
                    onChange={(option) => {
                      const payerId = option?.value ?? null;

                      setSelectedPayerId(payerId);
                      setSelectedRouteId(null);
                      setRoutes([]);
                      setWallets([]);
                      setMessage("");
                      setLastAction("");

                      if (payerId) {
                        fetchMissingRoutesForPayer(payerId);
                      }
                    }}
                    placeholder={t("admin_create_payer_wallet.fields.select_payer.placeholder")}
                    noOptionsMessage={() =>
                      loadingPayers
                        ? t("admin_create_payer_wallet.fields.select_payer.loading")
                        : t("admin_create_payer_wallet.fields.select_payer.no_options")
                    }
                  />
                </FormField>

                <FormField label={t("admin_create_payer_wallet.fields.select_route.label")}>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isDisabled={!selectedPayerId}
                    isLoading={loadingRoutes}
                    options={routeOptions}
                    value={selectedRouteOption}
                    onChange={(option) => {
                      setSelectedRouteId(option?.value ?? null);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={
                      selectedPayerId
                        ? t("admin_create_payer_wallet.fields.select_route.placeholder")
                        : t("admin_create_payer_wallet.fields.select_route.select_payer_first")
                    }
                    noOptionsMessage={() =>
                      loadingRoutes
                        ? t("admin_create_payer_wallet.fields.select_route.loading")
                        : t("admin_create_payer_wallet.fields.select_route.no_options")
                    }
                  />
                </FormField>

                <Button
                  onClick={handleCreateWallet}
                  disabled={!selectedPayerId || !selectedRouteId || submitting}
                  fullWidth
                >
                  {submitting
                    ? t("admin_create_payer_wallet.status.submitting")
                    : lastAction === "create" && message.startsWith("✅")
                    ? t("admin_create_payer_wallet.messages.success_short")
                    : lastAction === "create" && message.startsWith("❌")
                    ? `${t("admin_create_payer_wallet.messages.error_short")}: ${message.replace("❌ ", "")}`
                    : t("admin_create_payer_wallet.actions.create")}
                </Button>

                <Button
                  onClick={() => fetchWalletsForPayer(selectedPayerId)}
                  disabled={!selectedPayerId || loadingWallets}
                  fullWidth
                >
                  {loadingWallets
                    ? t("admin_create_payer_wallet.status.loading_wallets")
                    : t("admin_create_payer_wallet.actions.refresh_wallets")}
                </Button>
              </Stack>
            </Card>

            <StatusMessage message={message} />

            {selectedPayerId && (
              <Card>
                <Stack gap="3">
                  <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700 }}>
                    {t("admin_create_payer_wallet.wallets.title")}
                  </h2>

                  {wallets.length === 0 ? (
                    <div style={{ opacity: 0.7 }}>
                      {t("admin_create_payer_wallet.wallets.empty")}
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "8px 0" }}>
                              {t("admin_create_payer_wallet.wallets.columns.walletname")}
                            </th>
                            <th style={{ textAlign: "left", padding: "8px 0" }}>
                              {t("admin_create_payer_wallet.wallets.columns.route_id")}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {wallets.map((wallet) => (
                            <tr key={wallet.wallet_id}>
                              <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                                {wallet.walletname || "-"}
                              </td>
                              <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                                {wallet.route_id || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}